/**
 * Feedback Slice
 *
 * Stores explicit (free-text + chip) and implicit (behavioral) feedback events,
 * and the AI-generated taste profile that is periodically digested from them.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RejectionReason } from './userPreferencesSlice';
import { loadTasteProfile, loadAllFeedbackEvents } from '../services/feedbackSyncService';

// === Types ===

export type FeedbackEventType = 'explicit' | 'implicit';

export type ExplicitAction = 'remove' | 'regenerate';

export type ImplicitAction =
  | 'swap'
  | 'servings_change'
  | 'silent_regen'
  | 'kept_through_regen'
  | 'quick_confirm'
  | 'heavy_edit_confirm'
  | 'replaced_with_freetext'
  | 'meal_completed'
  | 'meal_rated';

export type FeedbackAction = ExplicitAction | ImplicitAction;

export interface FeedbackEvent {
  id: string;
  type: FeedbackEventType;
  action: FeedbackAction;
  recipeId?: string;
  recipeName?: string;
  detail?: string;
  quickReason?: RejectionReason;
  date: string;
  weekStartDate: string;
}

export interface TasteProfileInsight {
  date: string;
  insight: string;
}

export interface TasteProfileChangeLogEntry {
  version: number;
  date: string;
  summary: string;
}

export interface TasteProfile {
  version: number;
  lastUpdated: string;
  feedbackCount: number;
  preferences: Record<string, string>;
  recentInsights: TasteProfileInsight[];
  changeLog: TasteProfileChangeLogEntry[];
}

export interface FeedbackState {
  events: FeedbackEvent[];
  tasteProfile: TasteProfile | null;
  lastDigestedIndex: number;
}

const MAX_LOCAL_EVENTS = 50;

const initialState: FeedbackState = {
  events: [],
  tasteProfile: null,
  lastDigestedIndex: 0,
};

// === Async Thunks ===

export const downloadFeedbackFromCloud = createAsyncThunk(
  'feedback/downloadFromCloud',
  async (userId: string) => {
    const [profile, events] = await Promise.all([
      loadTasteProfile(userId),
      loadAllFeedbackEvents(userId),
    ]);
    return { profile, events };
  },
);

// === Slice ===

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    addFeedbackEvent: (state, action: PayloadAction<FeedbackEvent>) => {
      state.events.unshift(action.payload);
      if (state.events.length > MAX_LOCAL_EVENTS) {
        state.events = state.events.slice(0, MAX_LOCAL_EVENTS);
      }
    },

    setTasteProfile: (state, action: PayloadAction<TasteProfile>) => {
      state.tasteProfile = action.payload;
    },

    markEventsDigested: (state, action: PayloadAction<number>) => {
      state.lastDigestedIndex = action.payload;
    },

    clearFeedback: (state) => {
      state.events = [];
      state.tasteProfile = null;
      state.lastDigestedIndex = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadFeedbackFromCloud.fulfilled, (state, action) => {
      const { profile, events } = action.payload;
      if (profile) {
        state.tasteProfile = profile;
      }
      if (events.length > 0) {
        // Merge cloud events with local — deduplicate by id, keep most recent first
        const existingIds = new Set(state.events.map((e) => e.id));
        const newEvents = events.filter((e) => !existingIds.has(e.id));
        const merged = [...state.events, ...newEvents]
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, MAX_LOCAL_EVENTS);
        state.events = merged;
        state.lastDigestedIndex = merged.length;
      }
    });
  },
});

export const {
  addFeedbackEvent,
  setTasteProfile,
  markEventsDigested,
  clearFeedback,
} = feedbackSlice.actions;

// === Selectors ===

export const selectRecentFeedback = (state: { feedback: FeedbackState }) =>
  state.feedback.events;

export const selectFeedbackCount = (state: { feedback: FeedbackState }) =>
  state.feedback.events.length;

export const selectUndigestedCount = (state: { feedback: FeedbackState }) =>
  state.feedback.events.length - state.feedback.lastDigestedIndex;

export const selectTasteProfile = (state: { feedback: FeedbackState }) =>
  state.feedback.tasteProfile;

export default feedbackSlice.reducer;
